import { useNavigate } from "react-router-dom";
import { ShieldCheck, MapPin, Gauge, Store, BookmarkCheck, Tag } from "lucide-react";
import { currency } from "@/lib/status";
import { PROMO_TYPES, promoBadge } from "@/lib/promotions";

const FALLBACK = "https://images.unsplash.com/photo-1533630217389-3a5e4dff5683?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

const BODY_MAP = [["minivan", "Minivan"], ["van", "Van"], ["suv", "SUV"], ["crossover", "SUV"], ["sport utility", "SUV"], ["sedan", "Sedan"], ["pickup", "Truck"], ["truck", "Truck"], ["coupe", "Coupe"], ["convertible", "Convertible"], ["cabriolet", "Convertible"], ["hatch", "Hatchback"], ["wagon", "Wagon"]];
const bodyLabel = (b) => {
  const s = (b || "").toLowerCase();
  if (!s) return "New";
  const hit = BODY_MAP.find(([k]) => s.includes(k));
  return hit ? hit[1] : "New";
};

export function VehicleCard({ v, matched = false, promo = null }) {
  const navigate = useNavigate();
  const sold = v.availability !== "available";
  return (
    <div
      data-testid={`vehicle-card-${v.id}`}
      onClick={() => navigate(`/vehicles/${v.id}`)}
      className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-xl"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100 dark:bg-slate-800">
        <img src={v.image_url || FALLBACK} alt={`${v.make} ${v.model}`} onError={(e) => { e.target.src = FALLBACK; }} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/20 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
        {v.broker_verified && (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2.5} /> Broker Verified
          </span>
        )}
        {!v.broker_verified && v.source === "partner" && (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700">
            <Store className="h-3.5 w-3.5" strokeWidth={2.5} /> Dealer Listed
          </span>
        )}
        {sold && (
          <span className="absolute right-3 top-3 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-bold text-red-700">Unavailable</span>
        )}
        {promo && !sold && (
          <span className={`absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold shadow-sm ${PROMO_TYPES[promo.promo_type]?.cls || "bg-rose-600 text-white"}`} data-testid={`promo-ribbon-${v.id}`}>
            <Tag className="h-3.5 w-3.5" strokeWidth={2.5} /> {promoBadge(promo)}
          </span>
        )}
        {matched && !sold && (
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-800 shadow-sm" data-testid={`saved-match-ribbon-${v.id}`}>
            <BookmarkCheck className="h-3.5 w-3.5" strokeWidth={2.5} /> Matches your saved search
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-rose-600">{v.year} · {bodyLabel(v.body_type)}</p>
            <h3 className="mt-1 font-display text-lg font-bold tracking-tight text-slate-900 dark:text-slate-100">{v.make} {v.model}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">{v.trim} {v.color && `· ${v.color}`}</p>
          </div>
          <div className="text-right">
            <p className="font-mono text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">MSRP</p>
            <p className="font-mono text-lg font-bold text-slate-900 dark:text-slate-100">{currency(v.msrp)}</p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-4 border-t border-slate-100 dark:border-slate-800 pt-3 text-xs text-slate-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {v.dealer_location || "CA"}</span>
          <span className="inline-flex items-center gap-1"><Gauge className="h-3.5 w-3.5" /> Brand new</span>
        </div>
      </div>
    </div>
  );
}
